<template>
  <b-container
    :fluid="true"
    class="px-0"
    :style="{ margin: (loading || error) ? '0 !important' : 'auto'}"
    v-cloak
  >
    <form @submit.prevent="valid">
      <div v-if="loading" class="min-vh-100 d-flex align-items-center justify-content-center">
        <Loading />
      </div>
      <div v-else-if="error" class="min-vh-100 d-flex align-items-center justify-content-center">
        <div style="max-width: 400px;">
          <h4 class="text-2xl text-gray-500 font-weight-bold">
            Manifest Not Found
          </h4>
          <p class="text-gray-700">
            Manifest with id <span class="badge badge-primary">{{ $route.params.id }}</span> could
            not be found. Make sure that there are no typos in the Manifest id.
          </p>
          <b-button
            class="d-flex align-items-center mx-auto"
            @click="$router.go(-1)"
          >
            <b-icon-chevron-left font-scale="0.8" />
            BACK
          </b-button>
        </div>
      </div>
      <fieldset v-else :disabled="can('Manifest Details', 'read only')">
      <b-row class="px-3">
        <b-col
          cols="auto"
          class="pr-0 position-fixed d-none d-md-flex align-items-center"
          style="top: 10px; z-index: 1; height: calc(100vh - 20px);"
        >
          <div>
            <ShipmentNavProgress
              :card-names="cardNamesOrdered"
            ></ShipmentNavProgress>
          </div>
        </b-col>
        <b-col class="col ml-5" lg="8">
          <ShipmentPrototypeCard name="exporter" v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>EXPORTER/SHIPPER</h6>
            </template>
            <template v-slot:body>
              <div class="mx-5 pb-3">
                <v-select
                  @input="setExporter"
                  v-model="selectedExporter"
                  :filter="fuseSearch"
                  :options="agents"
                  :get-option-label="(option) => option.name"
                >
                  <template #option="{name, airportCode }">
                    <cite>{{ name }} - {{ airportCode }}</cite>
                  </template>
                </v-select>
                <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="exporterFirst">First Name
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="exporterFirst"
                      name="exporterFirst"
                      type="text"
                      v-model="response.exporterFirstName"
                    >
                  </b-col>

                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="exporterLast">Last Name
                    </label>
                    <input
                      class="form-control text-center"
                      id="exporterLast"
                      name="exporterLast"
                      type="text"
                      v-model="response.exporterLastName"
                    >
                  </b-col>
                </b-row>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="exporter-address">
                    Address
                  </label>
                  <input
                    required
                    class="form-control text-center"
                    id="exporter-address"
                    name="exporter-address"
                    type="text"
                    v-model="response.exporterAddress"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="exporter-country">
                    Country
                  </label>
                  <!-- <input
                    required
                    class="form-control text-center"
                    id="exporter-country"
                    name="exporter-country"
                    type="text"
                    v-model="response.exporterCountry"
                  > -->
                  <country-select class="form-control text-center"
                  required
                  style="color:gray;"
                  id="exporter-country"
                  countryName="true"
                  placeholder=""
                  v-model="response.exporterCountry" :country="response.exporterCountry"
                  topCountry="US" />
                </div>
                <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="exporter-city">City</label>
                    <input
                      required
                      class="form-control text-center"
                      id="exporter-city"
                      name="exporter-city"
                      type="text"
                      v-model="response.exporterCity"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="exporter-state">State</label>
                    <input
                      required
                      class="form-control text-center"
                      id="exporter-state"
                      name="exporter-state"
                      type="text"
                      v-model="response.exporterState"
                      @change="updateFTZ"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="exporter-zip-code">
                      Zone/Zip Code
                    </label>
                    <input
                      class="form-control text-center"
                      id="exporter-zip-code"
                      name="exporter-zip-code"
                      type="text"
                      v-model="response.exporterZipCode"
                    >
                  </b-col>
                </b-row>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="exporter-email">Email</label>
                  <input
                    class="form-control text-center"
                    id="exporter-email"
                    name="exporter-email"
                    type="email"
                    v-model="response.exporterEmail"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="exporter-phone">Phone</label>
                  <input
                    required
                    class="form-control text-center"
                    id="exporter-phone"
                    name="exporter-phone"
                    type="text"
                    v-model="response.exporterPhone"
                  >
                </div>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <ShipmentPrototypeCard name="importer" v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>IMPORTER/CONSIGNEE</h6>
            </template>
            <template v-slot:body>
              <div class="mx-5 pb-3">
                <v-select
                  @input="setImporter"
                  v-model="selectedImporter"
                  :filter="fuseSearch"
                  :options="agents"
                  :get-option-label="(option) => option.name"
                >
                  <template #option="{name, airportCode }">
                    <cite>{{ name }} - {{ airportCode }}</cite>
                  </template>
                </v-select>
                 <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="importerFirst">
                      First Name
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="importerFirst"
                      name="importerFirst"
                      type="text"
                      v-model="response.importerFirstName"
                    >
                  </b-col>

                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="importerLast">Last Name
                    </label>
                    <input
                      class="form-control text-center"
                      id="importerLast"
                      name="importerLast"
                      type="text"
                      v-model="response.importerLastName"
                    >
                  </b-col>
                </b-row>

                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="importer-address">
                    Address
                  </label>
                  <input
                    required
                    class="form-control text-center"
                    id="importer-address"
                    name="importer-address"
                    type="text"
                    v-model="response.importerAddress"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="importer-country">
                    Country
                  </label>
                  <!-- <input
                    required
                    class="form-control text-center"
                    id="importer-country"
                    name="importer-country"
                    type="text"
                    v-model="response.importerCountry"
                    @change="loadWarehouse()"
                  > -->
                  <country-select class="form-control text-center"
                  required
                  style="color:gray;"
                  id="importer-country"
                  countryName="true"
                  placeholder=""
                  v-model="response.importerCountry" :country="response.importerCountry"
                  topCountry="US"
                  @input="loadWarehouse()"
                  />
                </div>
                <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="importer-city">City</label>
                    <input
                      required
                      class="form-control text-center"
                      id="importer-city"
                      name="importer-city"
                      type="text"
                      v-model="response.importerCity"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="importer-state">State</label>
                    <input
                      required
                      class="form-control text-center"
                      id="importer-state"
                      name="importer-state"
                      type="text"
                      v-model="response.importerState"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="importer-zipcode">
                      Zone/Zip Code
                    </label>
                    <input
                      class="form-control text-center"
                      id="importer-zipcode"
                      name="importer-zipcode"
                      type="text"
                      v-model="response.importerZipCode"
                    >
                  </b-col>
                </b-row>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="importer-email">
                    Email
                  </label>
                  <input
                    class="form-control text-center"
                    id="importer-email"
                    name="importer-email"
                    type="email"
                    v-model="response.importerEmail"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="importer-phone">Phone</label>
                  <input
                    required
                    class="form-control text-center"
                    id="importer-phone"
                    name="importer-phone"
                    type="text"
                    v-model="response.importerPhone"
                  >
                </div>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <ShipmentPrototypeCard name="notify" v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>NOTIFY PARTY</h6>
            </template>
            <template v-slot:body>
              <div class="mx-5 pb-3">
                <v-select
                  @input="setNotifyParty"
                  v-model="selectedNotifyParty"
                  :filter="fuseSearch"
                  :options="agents"
                  :get-option-label="(option) => option.name"
                >
                  <template #option="{name, airportCode }">
                    <cite>{{ name }} - {{ airportCode }}</cite>
                  </template>
                </v-select>
                <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="notify-party-First">
                      First Name
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="notify-party-First"
                      name="notify-party-First"
                      type="text"
                      v-model="response.notifyPartyFirstName"
                    >
                  </b-col>

                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="notify-party-Last">Last Name
                    </label>
                    <input
                      class="form-control text-center"
                      id="notify-party-Last"
                      name="notify-party-Last"
                      type="text"
                      v-model="response.notifyPartyLastName"
                    >
                  </b-col>
                </b-row>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="notifyParty-address">
                    Address
                  </label>
                  <input
                    required
                    class="form-control text-center"
                    id="notify-party-address"
                    name="notify-party-address"
                    type="text"
                    v-model="response.notifyPartyAddress"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="notify-party-country">
                    Country
                  </label>
                  <!-- <input
                    required
                    class="form-control text-center"
                    id="notify-party-country"
                    name="notify-party-country"
                    type="text"
                    v-model="response.notifyPartyCountry"
                  > -->
                  <country-select class="form-control text-center"
                  required
                  style="color:gray;"
                  id="notify-party-country"
                  countryName="true"
                  placeholder=""
                  v-model="response.notifyPartyCountry" :country="response.notifyPartyCountry"
                  topCountry="US" />
                </div>

                <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="notify-party-city">
                      City</label>
                    <input
                      required
                      class="form-control text-center"
                      id="notify-party-city"
                      name="notify-party-city"
                      type="text"
                      v-model="response.notifyPartyCity"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="notify-party-state">
                      State</label>
                    <input
                      required
                      class="form-control text-center"
                      id="notify-party-state"
                      name="notify-party-state"
                      type="text"
                      v-model="response.notifyPartyState"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="notify-party-zipcode">
                      Zone/Zip Code
                    </label>
                    <input
                      class="form-control text-center"
                      id="notify-party-zipcode"
                      name="notify-party-zipcode"
                      type="text"
                      v-model="response.notifyPartyZipCode"
                    >
                  </b-col>
                </b-row>

                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="notify-party-email">
                    Email
                  </label>
                  <input
                    class="form-control text-center"
                    id="notify-party-email"
                    name="notify-party-email"
                    type="email"
                    v-model="response.notifyPartyEmail"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="notify-party-phone">
                    Phone
                  </label>
                  <input
                    required
                    class="form-control text-center"
                    id="notify-party-phone"
                    name="notify-party-phone"
                    type="text"
                    v-model="response.notifyPartyPhone"
                  >
                </div>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <ShipmentPrototypeCard name="forward"
           v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>FORWARDING AGENT</h6>
            </template>
            <template v-slot:body>
              <div class="mx-5 pb-3">
                <v-select
                  @input="setAgent"
                  v-model="selectedAgent"
                  :filter="fuseSearch"
                  :options="agents"
                  :get-option-label="(option) => option.name"
                >
                  <template #option="{name, airportCode }">
                    <cite>{{ name }} - {{ airportCode }}</cite>
                  </template>
                </v-select>
                <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="forwarding-agent-First">
                      First Name
                    </label>
                    <input
                      class="form-control text-center"
                      id="forwarding-agent-First"
                      name="forwarding-agent-First"
                      type="text"
                      v-model="response.forwardingAgentFirstName"
                    >
                  </b-col>

                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="forwarding-agent-Last">
                      Last Name</label>
                    <input
                      class="form-control text-center"
                      id="forwarding-agent-Last"
                      name="forwarding-agent-Last"
                      type="text"
                      v-model="response.forwardingAgentLastName"
                    >
                  </b-col>
                </b-row>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="forwarding-agent-address">
                    Address
                  </label>
                  <input
                    required
                    class="form-control text-center"
                    id="forwarding-agent-address"
                    name="forwarding-agent-address"
                    type="text"
                    v-model="response.forwardingAgentAddress"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="forwarding-agent-country">
                    Country
                  </label>
                  <!-- <input
                    required
                    class="form-control text-center"
                    id="forwarding-agent-country"
                    name="forwarding-agent-country"
                    type="text"
                    v-model="response.forwardingAgentCountry"
                  > -->
                  <country-select class="form-control text-center"
                  required
                  style="color:gray;"
                  id="forwarding-agent-country"
                  countryName="true"
                  placeholder=""
                  v-model="response.forwardingAgentCountry"
                  :country="response.forwardingAgentCountry"
                  topCountry="US" />
                </div>

                <b-row>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="forwarding-agent-city">
                      City</label>
                    <input
                      required
                      class="form-control text-center"
                      id="forwarding-agent-city"
                      name="forwarding-agent-city"
                      type="text"
                      v-model="response.forwardingAgentCity"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="forwarding-agent-state">
                      State</label>
                    <input
                      required
                      class="form-control text-center"
                      id="forwarding-agent-state"
                      name="forwarding-agent-state"
                      type="text"
                      v-model="response.forwardingAgentState"
                    >
                  </b-col>
                  <b-col class="floating-label-container">
                    <label class="col-form-label floating-label" for="forwarding-agent-zipcode">
                      Zone/Zip Code
                    </label>
                    <input
                      class="form-control text-center"
                      id="forwarding-agent-zipcode"
                      name="forwarding-agent-zipcode"
                      type="text"
                      v-model="response.forwardingAgentZipCode"
                    >
                  </b-col>
                </b-row>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="fmc">
                   FMC #
                  </label>
                  <input
                    required
                    class="form-control text-center"
                    id="fmc"
                    name="fmc"
                    type="text"
                    v-model="response.forwardingAgentFmc"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="forwarding-agent-email">
                    Email
                  </label>
                  <input
                    class="form-control text-center"
                    id="forwarding-agent-email"
                    name="forwarding-agent-email"
                    type="email"
                    v-model="response.forwardingAgentEmail"
                  >
                </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="forwarding-agent-phone">
                    Phone
                  </label>
                  <input
                    class="form-control text-center"
                    id="forwarding-agent-phone"
                    name="forwarding-agent-phone"
                    type="text"
                    v-model="response.forwardingAgentPhone"
                  >
                </div>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <ShipmentPrototypeCard name="terms"
           v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>TERMS AND RELEASE</h6>
            </template>
            <template v-slot:body>
              <div class="mx-5 pb-3">
                <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="incoterms">
                      Instructions
                    </label>
                    <b-form-select
                      required
                      id="incoterms"
                      v-model="response.incoterm"
                      :options="incotermTypes"
                      class="text-center"
                      @change="updateIncotermSelected"
                    ></b-form-select>
                  </div>
                <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="freight-paymentType">
                      Freight Payment Type
                    </label>
                    <b-form-select
                      required
                      id="freight-paymentType"
                      v-model="response.freightPaymentType"
                      :options="paymentTypes"
                      class="text-center"
                    ></b-form-select>
                  </div>
                <div class="floating-label-container">
                  <label class="col-form-label floating-label" for="freight-payable">
                    Freight Payable At
                  </label>
                  <b-form-select
                      required
                      id="freight-payable"
                      v-model="response.freightPayable"
                      :options="payableLocations"
                      class="text-center"
                    ></b-form-select>
                </div>
                <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="release-type">
                      Freight Release Type
                    </label>
                    <b-form-select
                      required
                      id="release-type"
                      v-model="response.freightRelease"
                      :options="releaseType"
                      class="text-center"
                    ></b-form-select>
                </div>
                <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="delivery-type">
                      Delivery Type
                    </label>
                    <b-form-select
                      required
                      id="delivery-type"
                      v-model="response.deliveryType"
                      :options="deliveryTypes"
                      class="text-center"
                    ></b-form-select>
                  </div>
                <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="numberBOL">
                      Copies of Original BOLs
                    </label>
                    <b-form-input
                      id="numberBOL"
                      class="text-center"
                      type="number"
                      min="0.0"
                      v-model="response.numberBOL"
                      number
                    ></b-form-input>
                  </div>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <ShipmentPrototypeCard name="shipping" v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>SHIPPING</h6>
            </template>
            <template v-slot:body>
              <div class="pb-3">
                <div class="mx-5">
                  <div>
                    <div v-if="$route.params.id">
                      <label class="col-form-label floating-label" for="bill-of-lading">
                        Booking #
                      </label>
                      <label class="form-control col-form-label floating-label"
                      for="bill-of-lading">{{ response.billOfLading }}
                      </label>
                    </div>
                    <div v-else>
                      <label class="col-form-label floating-label" for="bill-of-lading">
                        Booking #
                      </label>
                      <input
                        required
                        class="form-control text-center"
                        id="billOfLading"
                        name="billOfLading"
                        type="text"
                        v-model="response.billOfLading"
                      >
                    </div>
                    <!-- <label class="form-control text-center">
                      {{response.billOfLading}}
                    </label> -->
                  </div>
                  <!-- <div class="floating-label-container">
                    <div v-if="$route.params.id">
                      <label class="col-form-label floating-label" for="fileno">
                        BOL File #
                      </label>
                      <label class="form-control col-form-label floating-label" for="fileno">
                        {{ response.fileno }}
                      </label>
                    </div>
                    <div v-else>
                      <label class="col-form-label floating-label" for="fileno">
                        BOL File #
                      </label>
                      <input
                        required
                        class="form-control text-center"
                        id="fileno"
                        name="fileno"
                        type="text"
                        v-model="response.fileno"
                      >
                    </div>
                  </div> -->
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="aes-itn">
                      AES ITN #
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="aes-itn"
                      name="aes-itn"
                      type="text"
                      v-model="response.aesITN"
                    >
                  </div>
                  <!-- <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="bond">
                      Bond #
                    </label>
                    <input
                      class="form-control text-center"
                      id="bond"
                      name="bond"
                      type="text"
                      v-model="response.bond"
                    >
                  </div> -->
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="FTZ">
                      Point(State) of Origin or FTZ #
                    </label>
                    <input
                      class="form-control text-center"
                      id="FTZ"
                      name="FTZ"
                      type="text"
                      v-model="response.ftz"
                    >
                  </div>
                </div>
                <hr/>
                <div class="mx-5">
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="pre-carriage">
                      Pre-carriage
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="pre-carriage"
                      name="pre-carriage"
                      type="text"
                      v-model="response.preCarriage"
                    >
                  </div>
                  <b-row>
                    <b-col class="floating-label-container">
                     <label class="col-form-label floating-label" for="exporting-carrier">
                      Exporting Carrier
                      </label>
                      <!-- <input
                        required
                        class="form-control text-center"
                        id="exporting-carrier"
                        name="exporting-carrier"
                        type="text"
                        v-model="response.exportingCarriage"
                      > -->
                      <input
                        required
                        class="form-control text-center"
                        id="exporting-carrier"
                        name="exporting-carrier"
                        type="text"
                        list="my-list-id"
                        v-model="response.exportingCarriage"
                      >
                    <datalist id="my-list-id">
                      <option v-for="(carrier, index) in carriers" :key="index">
                        {{ carrier }}
                      </option>
                    </datalist>
                    </b-col>
                    <b-col class="floating-label-container">
                      <label class="col-form-label floating-label" for="exporting-Scac">SCAC
                      </label>
                      <input
                        required
                        class="form-control text-center"
                        id="exporting-Scac"
                        name="exporting-Scac"
                        type="text"
                        v-model="response.exportingScac"
                      >
                    </b-col>
                  </b-row>
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="vessel">
                      Vessel
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="vessel"
                      name="vessel"
                      type="text"
                      v-model="response.vessel"
                    >
                  </div>
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="voyage">
                      Voyage
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="voyage"
                      name="voyage"
                      type="text"
                      v-model="response.voyage"
                    >
                  </div>
                </div>
                <hr/>
                <div class="mx-5">
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="origin">
                      Origin
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="origin"
                      name="origin"
                      type="text"
                      v-model="response.origin"
                    >
                  </div>
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="loading-port">
                      Loading Port
                    </label>
                    <input
                      class="form-control text-center"
                      id="loading-port"
                      name="loading-port"
                      type="text"
                      v-model="response.loadingPort"
                    >
                  </div>
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="loading-pier">
                      Loading Pier
                    </label>
                    <input
                      class="form-control text-center"
                      id="loading-pier"
                      name="loading-pier"
                      type="text"
                      v-model="response.loadingPier"
                    >
                  </div>
                </div>
                <hr/>
                <div class="mx-5">
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="destination">
                      Destination
                    </label>
                    <input
                      required
                      class="form-control text-center"
                      id="destination"
                      name="destination"
                      type="text"
                      v-model="response.destination"
                    >
                  </div>
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="unloading-port">
                      Unloading Port
                    </label>
                    <input
                      class="form-control text-center"
                      id="unloading-port"
                      name="unloading-port"
                      type="text"
                      v-model="response.unloadingPort"
                    >
                  </div>
                </div>
                <hr/>
                <div class="mx-5">
                  <div class="floating-label-container">
                    <div class="d-inline-block">
                      <label class="col-form-label floating-label" for="shipped-date">
                        Shipped Date
                      </label>
                      <div>
                        <div class="d-inline-block">
                          <b-form-datepicker
                            required
                            reset-button
                            v-model="response.shippedDate"
                            class="normalize text-center"
                            :max="maxStartDate"
                          ></b-form-datepicker>
                        </div>
                        <div class="d-inline-block">
                          <b-form-timepicker
                            required
                            reset-button
                            v-model="shippedTime"
                            class="normalize"
                            locale="en"
                          ></b-form-timepicker>
                        </div>
                      </div>
                    </div>
                    <div class="d-inline-block">
                      <p>&nbsp;-&nbsp;</p>
                      <br>
                    </div>
                    <div class="d-inline-block">
                      <label class="col-form-label floating-label" for="arrival-date">
                        Arrival Date
                      </label>
                      <div>
                        <div class="d-inline-block">
                          <b-form-datepicker
                            required
                            reset-button
                            v-model="response.arrivalDate"
                            class="normalize text-center"
                            :min="minEndDate"
                          ></b-form-datepicker>
                        </div>
                        <div class="d-inline-block">
                          <b-form-timepicker
                            required
                            reset-button
                            v-model="arrivalTime"
                            class="normalize"
                            locale="en"
                          ></b-form-timepicker>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <ShipmentPrototypeCard name="warehouse" v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>WAREHOUSE</h6>
            </template>
            <template v-slot:body>
              <div class="px-5">
                <ContainerWidget
                :items="response.items"
                :billOfLading="response.billOfLading"
                :loadingFlag="warehouseLoading"
                :active="warehouseActive"
                >
                </ContainerWidget>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <ShipmentPrototypeCard name="notes" v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>NOTES</h6>
            </template>
            <template v-slot:body>
              <div class="mx-5 pb-3">
                <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="exporter-reference">
                      Exporter Reference
                    </label>
                    <input
                      class="form-control text-center"
                      id="exporter-reference"
                      name="exporter-reference"
                      type="text"
                      v-model="response.exporterReference"
                    >
                  </div>
                  <div class="floating-label-container">
                    <label class="col-form-label floating-label" for="routing-instruction">
                      Inland/Onward Routing Instruction
                    </label>
                    <input
                      class="form-control text-center"
                      id="routing-instruction"
                      name="routing-instruction"
                      type="text"
                      v-model="response.routingInstruction"
                    >
                  </div>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <!-- <ShipmentPrototypeCard name="notes" v-observe-visibility="visibilityChanged">
            <template v-slot:title>
              <h6>NOTES</h6>
            </template>
            <template v-slot:body>
              <ContainerPrototypeItemList
                :items="response.warehouseItems"
                @add="addNewItem"
                @remove="removeItem"
              ></ContainerPrototypeItemList>
            </template>
          </ShipmentPrototypeCard> -->
          <div v-if="$route.params.id">
            <ShipmentPrototypeCard name="submit" v-observe-visibility="visibilityChanged">
              <template v-slot:title>
                <h6>FINAL STEP</h6>
              </template>
              <template v-slot:body>
                <b-button type="update" block class="mb-2 shipment-create-button w-50 m-auto">
                  <b-icon-file-earmark-plus class="mr-2"></b-icon-file-earmark-plus>
                  UPDATE MANIFEST
                </b-button>
              </template>
            </ShipmentPrototypeCard>
          </div>
          <div v-else>
            <ShipmentPrototypeCard name="submit" v-observe-visibility="visibilityChanged">
              <template v-slot:title>
                <h6>FINAL STEP</h6>
              </template>
              <template v-slot:body>
                <b-button type="create" block class="mb-2 shipment-create-button w-50 m-auto">
                  <b-icon-file-earmark-plus class="mr-2"></b-icon-file-earmark-plus>
                  CREATE MANIFEST
                </b-button>
              </template>
            </ShipmentPrototypeCard>
          </div>
        </b-col>
        <b-col col class="h-100 position-sticky d-none d-lg-block" style="top: 10px;">
          <ShipmentPrototypeCard name="summary">
            <template v-slot:title>
              <h6 class="font-weight-bold">SUMMARY</h6>
            </template>
            <template v-slot:body>
              <hr class="mt-0"/>
              <div v-if="response.exporterFirstName && response.importerFirstName">
                <div>
                  {{ `${response.exporterFirstName} ${response.exporterLastName}` }}
                  <b-icon-arrow-right></b-icon-arrow-right>
                  {{ `${response.importerFirstName} ${response.importerLastName}` }}
                </div>
                <hr/>
              </div>
              <div v-if="response.origin && response.destination">
                <div>
                  {{ response.origin }}
                  <b-icon-arrow-right></b-icon-arrow-right>
                  {{ response.destination }}
                </div>
                <hr class="mb-0"/>
              </div>
            </template>
          </ShipmentPrototypeCard>
          <div v-if="$route.params.id">
            <b-button type="updateManifest" block class="shadow-sm mb-2 shipment-create-button">
              <b-icon-file-earmark-plus class="mr-2"></b-icon-file-earmark-plus>
              UPDATE MANIFEST
            </b-button>
          </div>
          <div v-else>
            <b-button type="createManifest" block class="shadow-sm mb-2 shipment-create-button">
              <b-icon-file-earmark-plus class="mr-2"></b-icon-file-earmark-plus>
              CREATE MANIFEST
            </b-button>
          </div>
          <div class="d-inline-flex mt-2">
            <b-button @click="reset" class="shipment-option" title="New Shipment">
              <b-icon-plus></b-icon-plus>
            </b-button>
            <b-button @click="copy" class="shipment-option" title="Copy to New">
              <b-icon-files></b-icon-files>
            </b-button>
            <b-button
              v-b-modal.print
              class="shipment-option"
              title="Print"
            >
              <b-icon-printer></b-icon-printer>
            </b-button>

             <b-modal id="print" ref="printModal" title="Manifest Files" hide-footer>
                <b-button
                  block class="shadow-sm mb-2 shipment-create-button"
                  @click="hideModal($refs.MasterBOL);"
                  title="MasterBOL"
                >
                 Master Bill of Lading
                </b-button>

                <b-button
                  block class="shadow-sm mb-2 shipment-create-button"
                  @click="hideModal($refs.DockReceipt);"
                  title="DockReceipt"
                >
                Dock Receipt
                </b-button>

                <b-button
                  block class="shadow-sm mb-2 shipment-create-button"
                  @click="hideModal($refs.PrintManifest);"
                  title="PrintManifest"
                >
                Manifest
                </b-button>
              </b-modal>

              <b-modal id="confirmation" ref="confirmationModal" centered title="" hide-footer
              hide-header>
                <div v-if="processing">
                  <Loading />
                </div>
                <div v-else-if="processingError">
                  <div class="d-flex align-items-center justify-content-center">
                    <Cross />
                  </div>
                  <div class="d-flex align-items-center justify-content-center">
                    <h2>Failure</h2>
                  </div>
                  <div class="d-flex align-items-center justify-content-center">
                    {{ processingError }}
                  </div>
                </div>
                <div v-else>
                  <div class="d-flex align-items-center justify-content-center">
                    <Checkmark />
                  </div>
                  <div class="d-flex align-items-center justify-content-center">
                    <h2>Done!</h2>
                  </div>
                </div>
              </b-modal>
            <MasterBOL name="html2Pdf" :response="response" :currentUser="currentUser"
             ref="MasterBOL" />
            <DockReceipt name="html2Pdf" :response="response" :currentUser="currentUser"
             ref="DockReceipt" />
             <PrintManifest name="html2Pdf" :response="response" :currentUser="currentUser"
             ref="PrintManifest" />
          </div>
        </b-col>
      </b-row>
      </fieldset>
    </form>
  </b-container>
</template>

<script>
// import { nanoid } from 'nanoid';
import moment from 'moment';
import 'moment-timezone';
import Fuse from 'fuse.js';
import Vue from 'vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import vueCountryRegionSelect from 'vue-country-region-select';
import MasterBOL from '../components/MasterBOL.vue';
import DockReceipt from '../components/DockReceipt.vue';
import PrintManifest from '../components/PrintManifest.vue';
import ShipmentPrototypeCard from '../components/ShipmentPrototypeCard.vue';
import ShipmentNavProgress from '../components/ShipmentNavProgress.vue';
// import ContainerPrototypeItemList from '../components/ContainerPrototypeItemList.vue';
import ContainerWidget from '../components/ContainerWidget.vue';
import Loading from '../components/Loading.vue';
import Checkmark from '../components/Checkmark.vue';
import Cross from '../components/Cross.vue';
// import { getAgent } from '../api/shipment';
import { getOffice } from '../api/admin';

import {
  postManifest, editManifest, getManifestById, warehouse,
} from '../api/manifest';

Vue.use(vueCountryRegionSelect);

export default {
  name: 'ShipmentPrototype',
  data() {
    return this.initialState();
  },
  computed: {
    cardNamesOrdered() {
      return Object.values(this.cardNames).sort((a, b) => a.order - b.order);
    },
    mostCommonReference() {
      const counter = {};
      this.items.forEach((elem) => {
        if (elem.reference in counter) {
          counter[elem.reference] += elem.amount;
        } else {
          counter[elem.reference] = elem.amount;
        }
      });

      let max = -1;
      let maxRef = '';
      Object.entries(counter).forEach(([key, val]) => {
        if (val >= max) {
          maxRef = key;
          max = val;
        }
      });
      return max === -1 ? '...' : `${maxRef}...`;
    },
    totalCount() {
      return this.items.reduce((acc, curr) => acc + curr.amount, 0);
    },
    totalWeight() {
      return this.items.reduce((acc, curr) => acc + curr.weight, 0);
    },
    totalVolume() {
      return this.items.reduce((acc, curr) => acc + curr.volume, 0);
    },
    outstandingBalance() {
      return this.charges.reduce((acc, curr) => acc + curr.amount, 0)
                    - this.payments.reduce((acc, curr) => acc + curr.amount, 0);
    },
    minEndDate() {
      return this.response.shippedDate;
    },
    maxStartDate() {
      return this.response.arrivalDate;
    },
  },
  methods: {
    initialState() {
      return {
        currentUser: JSON.parse(localStorage.getItem('user')),
        warehouseActive: '',
        deliveryTypes: [
          { value: 'porttoport', text: 'port to port' },
          { value: 'preclearance', text: 'pre-clearance' },
          { value: 'doortodoor', text: 'door to door' },
        ],
        incotermTypes: [
          { value: 'cfr', text: 'CFR', type: 'prepaid' },
          { value: 'cif', text: 'CIF', type: 'prepaid' },
          { value: 'cpt', text: 'CPT', type: 'prepaid' },
          { value: 'cip', text: 'CIP', type: 'prepaid' },
          { value: 'dap', text: 'DAP', type: 'prepaid' },
          { value: 'ddp', text: 'DDP', type: 'prepaid' },
          { value: 'dpu', text: 'DPU', type: 'prepaid' },
          { value: 'exw', text: 'EXW', type: 'collect' },
          { value: 'fob', text: 'FOB', type: 'collect' },
          { value: 'fca', text: 'FCA', type: 'collect' },
          { value: 'fas', text: 'FAS', type: 'collect' },
        ],
        paymentTypes: [
          { value: 'prepaid', text: 'Prepaid' },
          { value: 'collect', text: 'Collect' },
        ],
        payableLocations: [
          { value: 'origin', text: 'Origin' },
          { value: 'destination', text: 'Destination' },
        ],
        releaseType: [
          { value: 'express', text: 'Express' },
          { value: 'telex', text: 'Telex' },
          { value: 'original', text: 'Original' },
        ],
        carriers: [
          'Atlantic Container Line',
          'Alianca',
          'Alaska Marine Lines',
          'Australia National Line',
          'American President Lines',
          'Arkas Container Transport S.A.',
          'Atlantic Ro-Ro Carriers Inc',
          'Bahri / National Shipping Company of Saudi Arabia',
          'Bermuda Container Line',
          'Central Gulf Lines, Inc',
          'Compagnia Chilena de Navigacion Interoceanica SA',
          'Chinese-Polish Joint Stock Shipping Company',
          'Compagnie Maritime d Affretement Compagnie Generale Maritime',
          'Cheng Lie Navigation Co.,Ltd',
          'COSCO Container Lines',
          'Crowley',
          'Compania Sud Americana de Vapores',
          'CSAV Norasia',
          'China Shipping Container Lines Co',
          'Delmas',
          'Dole Ocean Cargo Express',
          'Ecuadorian Line',
          'Eimskip',
          'Emirates Shipping Line',
          'Eukor',
          'Evergreen Line',
          'Far Eastern Shipping Company',
          'Galborg',
          'Grieg Star Shipping',
          'Grimaldi',
          'Gold Star Line Ltd.',
          'Great White Fleet',
          'Great Eastern Shipping Inc.',
          'Hamburg Sud',
          'Hapag Lloyd Container Line',
          'Hyundai Merchant Marine Co., Ltd.',
          'Hoegh Autoliners',
          'Horizon Lines',
          'Hyde Shipping',
          'Independent Container Line',
          'Industrial Maritime Carriers (Intermarine)',
          'Interocean Lines',
          'Kawasaki Kisen Kaisha, Ltd.',
          'King Ocean Sercies',
          'Korea Marine Transport Co., Ltd.',
          'Companhia Libra de Navegacao',
          'Liberty Global Logistics, LLC',
          'Linea Messina',
          'Maersk Line',
          'Marfret Compagnie Maritime',
          'Maruba',
          'Matson',
          'MCC Transport Pte. Ltd.',
          'Malaysia International Shipping Corporation Berhad',
          'Mediterranean Shipping Company',
          'Nile Dutch Africa Line',
          'Neptune Shipping Line',
          'Nordana Line',
          'Ocean Network Express (ONE Line)',
          'Orient Overseas Container Line Ltd.',
          'Pasha Hawaii Transport Lines',
          'Pacific International Lines',
          'PM&O Lines',
          'Polynesia Line',
          'Rickmers Linie',
          'Safmarine',
          'Sallaum Lines',
          'Samskip, Inc.',
          'The Shipping Coporation of India Ltd.',
          'Seaboard Marine',
          'SeaFreight Line',
          'Seago Line',
          'Siem Car Carriers AS',
          'Sinokor Merchant Marine Co.,Ltd',
          'Swire Shipping',
          'Thorco Shipping',
          'TOHO Shipping Co., Ltd',
          'TOTE Maritime',
          'Turkon Line Inc',
          'Universal Africa Lines',
          'US Lines',
          'West European Container Lines',
          'Westwood Shipping Lines',
          'Wan Hai Lines',
          'World Logistics Service (USA) Inc',
          'Wallenius Wilhelmsen Logistics',
          'Yang Ming Line',
          'ZIM Integrated Shipping Services Ltd',

        ],
        warehouseLoading: false,
        arrivalTime: '',
        shippedTime: '',
        agents: [],
        selectedExporter: '',
        selectedImporter: '',
        selectedNotifyParty: '',
        selectedAgent: '',
        response: {
          items: {
            warehouse: [],
            container: [],
          },
          // warehouseItems: [
          //   {
          //     id: 0,
          //     qty: '',
          //     commodity: '',
          //     description: '',
          //     flag: true,
          //   },
          // ],
          exporterFirstName: '',
          exporterLastName: '',
          exporterAddress: '',
          exporterCountry: '',
          exporterCity: '',
          exporterState: '',
          exporterZipCode: '',
          exporterPhone: '',
          exporterEmail: '',
          forwardingAgentFirstName: '',
          forwardingAgentLastName: '',
          forwardingAgentAddress: '',
          forwardingAgentCountry: '',
          forwardingAgentCity: '',
          forwardingAgentState: '',
          forwardingAgentZipCode: '',
          forwardingAgentFmc: '',
          forwardingAgentPhone: '',
          forwardingAgentEmail: '',
          importerFirstName: '',
          importerLastName: '',
          importerAddress: '',
          importerCountry: '',
          importerCity: '',
          importerState: '',
          importerZipCode: '',
          importerPhone: '',
          importerEmail: '',
          notifyPartyFirstName: '',
          notifyPartyLastName: '',
          notifyPartyAddress: '',
          notifyPartyCountry: '',
          notifyPartyCity: '',
          notifyPartyState: '',
          notifyPartyZipCode: '',
          notifyPartyPhone: '',
          notifyPartyEmail: '',
          billOfLading: '',
          exporterReference: '',
          routingInstruction: '',
          preCarriage: '',
          exportingCarriage: '',
          exportingScac: '',
          vessel: '',
          voyage: '',
          loadingPort: '',
          loadingPier: '',
          unloadingPort: '',
          deliveryType: 'porttoport',
          origin: '',
          destination: '',
          freightPaymentType: '',
          bond: '',
          ftz: '',
          numberBOL: 0,
          incoterm: '',
          freightPayable: '',
          freightRelease: 'express',
          fileno: '',
          arrivalDate: '',
          shippedDate: '',
          // Put warehouse stuff here
          containerSize: '',
          containerNumber: '',
          aesITN: '',
          seal: '',
          description: '',
        },
        loading: true,
        processing: false,
        processingError: false,
        error: false,
        cardNames: {
          exporter: {
            order: 0,
            title: 'Exporter',
            id: 'exporter',
            visible: false,
          },
          importer: {
            order: 1,
            title: 'Importer',
            id: 'importer',
            visible: false,
          },
          notify: {
            order: 2,
            title: 'Notify Party',
            id: 'notify',
            visible: false,
          },
          forward: {
            order: 3,
            title: 'Forwarding Agent',
            id: 'forward',
            visible: true,
          },
          terms: {
            order: 4,
            title: 'Terms&Releases',
            id: 'terms',
            visible: true,
          },
          shipping: {
            order: 5,
            title: 'Shipping',
            id: 'shipping',
            visible: false,
          },
          warehouse: {
            order: 6,
            title: 'Warehouse',
            id: 'warehouse',
            visible: false,
          },
          // marks: {
          //   order: 6,
          //   title: 'Marks',
          //   id: 'marks',
          //   visible: false,
          // },
          notes: {
            order: 7,
            title: 'Notes',
            id: 'notes',
            visible: false,
          },
          submit: {
            order: 8,
            title: 'Finish',
            id: 'submit',
            visible: false,
          },
        },
      };
    },
    setExporter(value) {
      if (value == null) {
        this.response.exporterFirstName = '';
        this.response.exporterLastName = '';
        this.response.exporterAddress = '';
        this.response.exporterCountry = '';
        this.response.exporterCity = '';
        this.response.exporterState = '';
        this.response.exporterZipCode = '';
        this.response.exporterAirportCode = '';
        this.response.exporterPhone = '';
        this.response.exporterEmail = '';
      } else {
        // this.response.exporterFirstName = value.firstName;
        // this.response.exporterLastName = value.lastName;
        this.response.exporterFirstName = value.name;
        this.response.exporterAddress = value.address;
        this.response.exporterCountry = value.country;
        this.response.exporterCity = value.city;
        this.response.exporterState = value.state;
        this.response.exporterZipCode = value.zipCode;
        this.response.exporterAirportCode = value.airportCode;
        this.response.exporterPhone = value.phone;
        this.response.exporterEmail = value.email;
      }
    },
    setImporter(value) {
      if (value == null) {
        this.response.importerFirstName = '';
        this.response.importerLastName = '';
        this.response.importerAddress = '';
        this.response.importerCountry = '';
        this.response.importerCity = '';
        this.response.importerState = '';
        this.response.importerZipCode = '';
        this.response.importerAirportCode = '';
        this.response.importerPhone = '';
        this.response.importerEmail = '';
        this.response.items.warehouse = [];
      } else {
        // this.response.importerFirstName = value.firstName;
        // this.response.importerLastName = value.lastName;
        this.response.importerFirstName = value.name;
        this.response.importerAddress = value.address;
        this.response.importerCountry = value.country;
        this.response.importerCity = value.city;
        this.response.importerState = value.state;
        this.response.importerZipCode = value.zipCode;
        this.response.exporterAirportCode = value.airportCode;
        this.response.importerPhone = value.phone;
        this.response.importerEmail = value.email;
        this.loadWarehouse();
      }
    },
    setNotifyParty(value) {
      if (value == null) {
        this.response.notifyPartyFirstName = '';
        this.response.notifyPartyLastName = '';
        this.response.notifyPartyAddress = '';
        this.response.notifyPartyCountry = '';
        this.response.notifyPartyCity = '';
        this.response.notifyPartyState = '';
        this.response.notifyPartyZipCode = '';
        this.response.notifyPartyAirportCode = '';
        this.response.notifyPartyPhone = '';
        this.response.notifyPartyEmail = '';
      } else {
        // this.response.notifyPartyFirstName = value.firstName;
        // this.response.notifyPartyLastName = value.lastName;
        this.response.notifyPartyFirstName = value.name;
        this.response.notifyPartyAddress = value.address;
        this.response.notifyPartyCountry = value.country;
        this.response.notifyPartyCity = value.city;
        this.response.notifyPartyState = value.state;
        this.response.notifyPartyZipCode = value.zipCode;
        this.response.notifyPartyAirportCode = value.airportCode;
        this.response.notifyPartyPhone = value.phone;
        this.response.notifyPartyEmail = value.email;
      }
    },
    setAgent(value) {
      if (value == null) {
        this.response.forwardingAgentFirstName = '';
        this.response.forwardingAgentLastName = '';
        this.response.forwardingAgentAddress = '';
        this.response.forwardingAgentCountry = '';
        this.response.forwardingAgentCity = '';
        this.response.forwardingAgentState = '';
        this.response.forwardingAgentZipCode = '';
        this.response.forwardingAgentFmc = '';
        this.response.forwardingAgentAirportCode = '';
        this.response.forwardingAgentPhone = '';
        this.response.forwardingAgentEmail = '';
      } else {
        // this.response.forwardingAgentFirstName = value.firstName;
        // this.response.forwardingAgentLastName = value.lastName;
        this.response.forwardingAgentFirstName = value.name;
        this.response.forwardingAgentAddress = value.address;
        this.response.forwardingAgentCountry = value.country;
        this.response.forwardingAgentCity = value.city;
        this.response.forwardingAgentState = value.state;
        this.response.forwardingAgentZipCode = value.zipCode;
        this.response.forwardingAgentFmc = value.fmc;
        this.response.forwardingAgentAirportCode = value.airportCode;
        this.response.forwardingAgentPhone = value.phone;
        this.response.forwardingAgentEmail = value.email;
      }
    },
    fuseSearch(options, search) {
      const fuse = new Fuse(options, {
        keys: ['firstName', 'lastName', 'airportCode'],
        shouldSort: true,
      });
      return search.length
        ? fuse.search(search).map(({ item }) => item)
        : fuse.list;
    },
    loadWarehouse() {
      if (this.response.importerCountry !== '') {
        this.warehouseLoading = true;
        warehouse(this.response.importerCountry.toLowerCase().trim())
          .then((resp) => {
            console.log('Successfully GETed');
            console.log(resp);
            this.warehouseLoading = false;
            this.response.items.warehouse = resp.data.results;
          }).catch((err) => {
            console.log(err);
          });
      } else {
        this.response.items.warehouse = [];
      }
    },
    reset() {
      const agentlist = this.$data.agents;
      Object.assign(this.$data, this.initialState());
      delete this.$route.params.id;
      this.agents = agentlist;
      window.history.pushState({}, null, '#/manifest-proto');
      this.loading = false;
    },
    copy() {
      const agentlist = this.$data.agents;
      const copy = this.$data.response;
      const copyselectedAgent = this.$data.selectedAgent;
      const copyselectedExporter = this.$data.selectedExporter;
      const copyselectedImporter = this.$data.selectedImporter;
      const copyselectedNotifyParty = this.$data.selectedNotifyParty;
      Object.assign(this.$data, this.initialState());
      this.response.exporterFirstName = copy.exporterFirstName;
      this.response.exporterLastName = copy.exporterLastName;
      this.response.exporterAddress = copy.exporterAddress;
      this.response.exporterCountry = copy.exporterCountry;
      this.response.exporterCity = copy.exporterCity;
      this.response.exporterState = copy.exporterState;
      this.response.exporterZipCode = copy.exporterZipCode;
      this.response.exporterPhone = copy.exporterPhone;
      this.response.exporterEmail = copy.exporterEmail;

      this.response.forwardingAgentFirstName = copy.forwardingAgentFirstName;
      this.response.forwardingAgentLastName = copy.forwardingAgentLastName;
      this.response.forwardingAgentAddress = copy.forwardingAgentAddress;
      this.response.forwardingAgentCountry = copy.forwardingAgentCountry;
      this.response.forwardingAgentCity = copy.forwardingAgentCity;
      this.response.forwardingAgentState = copy.forwardingAgentState;
      this.response.forwardingAgentZipCode = copy.forwardingAgentZipCode;
      this.response.forwardingAgentFmc = copy.forwardingAgentFmc;
      this.response.forwardingAgentPhone = copy.forwardingAgentPhone;
      this.response.forwardingAgentEmail = copy.forwardingAgentEmail;

      this.response.importerFirstName = copy.importerFirstName;
      this.response.importerLastName = copy.importerLastName;
      this.response.importerAddress = copy.importerAddress;
      this.response.importerCountry = copy.importerCountry;
      this.response.importerCity = copy.importerCity;
      this.response.importerState = copy.importerState;
      this.response.importerZipCode = copy.importerZipCode;
      this.response.importerPhone = copy.importerPhone;
      this.response.importerEmail = copy.importerEmail;

      this.response.notifyPartyFirstName = copy.notifyPartyFirstName;
      this.response.notifyPartyLastName = copy.notifyPartyLastName;
      this.response.notifyPartyPhone = copy.notifyPartyPhone;
      this.response.notifyPartyEmail = copy.notifyPartyEmail;
      this.response.notifyPartyAddress = copy.notifyPartyAddress;
      this.response.notifyPartyCountry = copy.notifyPartyCountry;
      this.response.notifyPartyCity = copy.notifyPartyCity;
      this.response.notifyPartyState = copy.notifyPartyState;
      this.response.notifyPartyZipCode = copy.notifyPartyZipCode;

      this.response.incoterm = copy.incoterm;
      this.response.freightPaymentType = copy.freightPaymentType;
      this.response.freightPayable = copy.freightPayable;
      this.response.freightRelease = copy.freightRelease;
      this.response.deliveryType = copy.deliveryType;
      this.response.numberBOL = copy.numberBOL;

      this.agents = agentlist;
      this.selectedExporter = copyselectedExporter;
      this.selectedImporter = copyselectedImporter;
      this.selectedNotifyParty = copyselectedNotifyParty;
      this.selectedAgent = copyselectedAgent;

      delete this.$route.params.id;
      window.history.pushState({}, null, '#/manifest-proto');
      this.loading = false;
    },
    countryFormatter(country) {
      if (!country) {
        return '';
      }
      let arr = [];
      const exludeWords = ['of', 'the', 'and'];
      arr = country.split(' ');
      return arr.map((word, i) => (exludeWords.includes(word) && i !== 0 ? [word] : word.charAt(0).toUpperCase() + word.slice(1))).join(' ');
    },
    createManifest() {
      this.processing = true;
      this.processingError = false;
      this.$refs.confirmationModal.show();
      const copy = JSON.parse(JSON.stringify(this.response));
      copy.arrivalDate = `${copy.arrivalDate} ${this.arrivalTime}`;
      copy.arrivalDate = moment.tz(copy.arrivalDate, this.currentUser.timezone).utc().format('YYYY-MM-DD HH:mm:ss');
      copy.shippedDate = `${copy.shippedDate} ${this.shippedTime}`;
      copy.shippedDate = moment.tz(copy.shippedDate, this.currentUser.timezone).utc().format('YYYY-MM-DD HH:mm:ss');
      // copy.warehouseItems = copy.warehouseItems.map(({ id, ...rest }) => rest);
      const test = Object.keys(copy).reduce((acc, curr) => {
        if (typeof copy[curr] === 'string') {
          acc[curr] = copy[curr].toLowerCase().trim();
        } else {
          acc[curr] = copy[curr];
        }
        return acc;
      }, {});
      const data = { data: { ...test } };
      postManifest(data)
        .then((resp) => {
          console.log('Successfully POSTed');
          console.log(resp);
          this.processing = false;
          setTimeout(() => this.$refs.confirmationModal.hide(), 2000);
          this.$router.push(`/manifest-proto/${resp.data.id}`);
        }).catch((err) => {
          this.processingError = err.response.data.error;
          this.processing = false;
        });
    },
    updateManifest() {
      this.processing = true;
      this.processingError = false;
      this.$refs.confirmationModal.show();
      const copy = JSON.parse(JSON.stringify(this.response));
      copy.arrivalDate = `${copy.arrivalDate} ${this.arrivalTime}`;
      copy.arrivalDate = moment.tz(copy.arrivalDate, this.currentUser.timezone).utc().format('YYYY-MM-DD HH:mm:ss');
      copy.shippedDate = `${copy.shippedDate} ${this.shippedTime}`;
      copy.shippedDate = moment.tz(copy.shippedDate, this.currentUser.timezone).utc().format('YYYY-MM-DD HH:mm:ss');
      const test = Object.keys(copy).reduce((acc, curr) => {
        if (typeof copy[curr] === 'string') {
          acc[curr] = copy[curr].toLowerCase().trim();
        } else {
          acc[curr] = copy[curr];
        }
        return acc;
      }, {});
      const data = { data: { ...test } };
      editManifest(data)
        .then((resp) => {
          console.log('Successfully POSTed');
          console.log(resp);
          this.processing = false;
          setTimeout(() => this.$refs.confirmationModal.hide(), 2000);
          setTimeout(() => this.$router.go(0), 1000);
        }).catch((err) => {
          this.processingError = err.response.data.error;
          this.processing = false;
        });
    },
    visibilityChanged(isVisible, entry) {
      if (Object.prototype.hasOwnProperty.call(this.cardNames, entry.target.id)) {
        this.cardNames[entry.target.id].visible = isVisible;
      }
    },
    updateIncotermSelected(value) {
      const selected = this.incotermTypes.find((incoterm) => incoterm.value === value);
      if (selected.type === 'prepaid') {
        this.response.freightPaymentType = 'prepaid';
        this.response.freightPayable = 'origin';
      } else {
        this.response.freightPaymentType = 'collect';
        this.response.freightPayable = 'destination';
      }
    },
    updateFTZ() {
      this.response.ftz = this.response.exporterState;
    },
    // addNewItem() {
    //   this.response.warehouseItems.push({
    //     id: nanoid(6),
    //     qty: '',
    //     commodity: '',
    //     description: '',
    //     flag: true,
    //   });
    // },
    // removeItem(id) {
    //   this.response.warehouseItems.find(
    //     (item) => item.id === id,
    //   ).flag = false;
    //   // this.response.warehouseItems.splice(this.response.warehouseItems.findIndex(
    //   //   (item) => item.id === id,
    //   // ), 1);
    // },
    valid() {
      if (this.response.items.container.length === 0) {
        this.processingError = 'Need to add a container';
        this.$refs.confirmationModal.show();
        // <div v-if="containererror" class="text-danger">
        // <small>Need to add a container</small>
        // </div>
        return;
      }
      if (this.$route.params.id) {
        this.updateManifest();
      } else {
        this.createManifest();
      }
    },
    toggleHazard(idx) {
      const item = this.items.find((elem) => elem.id === idx);
      item.hazardous = !item.hazardous;
    },
    hideModal(file) {
      this.$refs.printModal.hide();
      file.generatePdf();
    },
  },
  watch: {
    $route(to, from) {
      const toDepth = to.path.split('/').length;
      const fromDepth = from.path.split('/').length;
      if (toDepth < fromDepth) {
        this.reset();
      }
    },
  },
  mounted() {
    this.initFormlessFloatingLabels();

    if (this.$route.params.id) {
      getManifestById(this.$route.params.id)
        .then((res) => {
          this.response.items.container = res.data.result.containers;
          this.response.exporterFirstName = res.data.result.exporter.firstName;
          this.response.exporterLastName = res.data.result.exporter.lastName;
          this.response.exporterAddress = res.data.result.exporter.address;
          this.response.exporterCountry = this.countryFormatter(res.data.result.exporter.country);
          this.response.exporterCity = res.data.result.exporter.city;
          this.response.exporterState = res.data.result.exporter.state;
          this.response.exporterZipCode = res.data.result.exporter.zipCode;
          this.response.exporterPhone = res.data.result.exporter.phone;
          this.response.exporterEmail = res.data.result.exporter.email;
          this.response.aesITN = res.data.result.exporter.aesITN;
          this.response.forwardingAgentFirstName = res.data.result.forwardingAgent.firstName;
          this.response.forwardingAgentLastName = res.data.result.forwardingAgent.lastName;
          this.response.forwardingAgentAddress = res.data.result.forwardingAgent.address;
          this.response.forwardingAgentCountry = this.countryFormatter(
            res.data.result.forwardingAgent.country,
          );
          this.response.forwardingAgentCity = res.data.result.forwardingAgent.city;
          this.response.forwardingAgentState = res.data.result.forwardingAgent.state;
          this.response.forwardingAgentZipCode = res.data.result.forwardingAgent.zipCode;
          this.response.forwardingAgentFmc = res.data.result.forwardingAgent.fmc;
          this.response.forwardingAgentPhone = res.data.result.forwardingAgent.phone;
          this.response.forwardingAgentEmail = res.data.result.forwardingAgent.email;
          this.response.importerFirstName = res.data.result.importer.firstName;
          this.response.importerLastName = res.data.result.importer.lastName;
          this.response.importerAddress = res.data.result.importer.address;
          this.response.importerCountry = this.countryFormatter(res.data.result.importer.country);
          this.response.importerCity = res.data.result.importer.city;
          this.response.importerState = res.data.result.importer.state;
          this.response.importerZipCode = res.data.result.importer.zipCode;
          this.response.importerPhone = res.data.result.importer.phone;
          this.response.importerEmail = res.data.result.importer.email;
          this.response.notifyPartyFirstName = res.data.result.notifyParty.firstName;
          this.response.notifyPartyLastName = res.data.result.notifyParty.lastName;
          this.response.notifyPartyAddress = res.data.result.notifyParty.address;
          this.response.notifyPartyCountry = this.countryFormatter(
            res.data.result.notifyParty.country,
          );
          this.response.notifyPartyCity = res.data.result.notifyParty.city;
          this.response.notifyPartyState = res.data.result.notifyParty.state;
          this.response.notifyPartyZipCode = res.data.result.notifyParty.zipCode;
          this.response.notifyPartyPhone = res.data.result.notifyParty.phone;
          this.response.notifyPartyEmail = res.data.result.notifyParty.email;
          this.response.billOfLading = res.data.result.billOfLading;
          this.response.exporterReference = res.data.result.manifestDetails.exporterReference;
          this.response.routingInstruction = res.data.result.manifestDetails.routingInstruction;
          this.response.preCarriage = res.data.result.manifestDetails.preCarriage;
          this.response.exportingCarriage = res.data.result.manifestDetails.exportingCarriage;
          this.response.exportingScac = res.data.result.manifestDetails.exportingScac;
          this.response.vessel = res.data.result.manifestDetails.vessel;
          this.response.voyage = res.data.result.manifestDetails.voyage;
          this.response.loadingPort = res.data.result.manifestDetails.loadingPort;
          this.response.loadingPier = res.data.result.manifestDetails.loadingPier;
          this.response.unloadingPort = res.data.result.manifestDetails.unloadingPort;
          this.response.deliveryType = res.data.result.manifestDetails.deliveryType;
          this.response.origin = res.data.result.manifestDetails.origin;
          this.response.destination = res.data.result.manifestDetails.destination;
          this.response.freightPaymentType = res.data.result.manifestDetails.freightPaymentType;
          this.response.ftz = res.data.result.manifestDetails.ftz;
          this.response.bond = res.data.result.manifestDetails.bond;
          this.response.numberBOL = res.data.result.manifestDetails.numberBOL;
          this.response.incoterm = res.data.result.manifestDetails.incoterm;
          this.response.freightPayable = res.data.result.manifestDetails.freightPayable;
          this.response.freightRelease = res.data.result.manifestDetails.freightRelease;
          this.response.fileno = res.data.result.manifestDetails.fileno;
          this.response.description = res.data.result.manifestDetails.description;
          // this.response.warehouseItems = res.data.result.manifestDetails.warehouseItems;

          this.response.arrivalDate = moment.tz(
            res.data.result.manifestDetails.arrivalDate, this.currentUser.timezone,
          );
          this.response.shippedDate = moment.tz(
            res.data.result.manifestDetails.shippedDate, this.currentUser.timezone,
          );

          this.arrivalTime = this.response.arrivalDate.format('HH:mm:ss');
          this.shippedTime = this.response.shippedDate.format('HH:mm:ss');
          this.response.arrivalDate = this.response.arrivalDate.format('YYYY-MM-DD');
          this.response.shippedDate = this.response.shippedDate.format('YYYY-MM-DD');

          this.loadWarehouse();
          // this.loading = false;
          // this.error = false;
        })
        .catch((err) => {
          console.log(err);
          this.loading = false;
          this.error = true;
        });
    } // else {
    //   this.loading = false;
    //   this.error = false;
    // }

    getOffice().then((resp) => {
      console.log('Successfully GETed');
      this.agents = resp.data.result;
      this.agents = this.agents.map((agent) => ({
        ...agent,
        country: this.countryFormatter(agent.country),
      }));
      this.loading = false;
      this.error = false;
    }).catch((err) => {
      console.log(err);
      this.loading = false;
      this.error = true;
    });
  },
  components: {
    // ContainerPrototypeItemList,
    vSelect,
    ContainerWidget,
    ShipmentNavProgress,
    ShipmentPrototypeCard,
    MasterBOL,
    DockReceipt,
    PrintManifest,
    Loading,
    Checkmark,
    Cross,
  },
};
</script>

<style scoped>
/*@import '../assets/styles/floatingLabel.scss';*/
</style>

<style>
html {
  scroll-behavior: smooth;
}

.shipment-create-button {
  background: #7da3c7;
  border: none;
}

.shipment-create-button:hover {
  background: #608cb3;
}

.shipment-option {
  height: 3em;
  width: 3em;
  padding: 0;
  border-radius: 50%;
  margin: 0 10px;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  background: #7da3c7;
  outline: none;
  border: none;
}

.shipment-option:hover {
  background: #608cb3;
}
</style>
